import React from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

/**
 * Chats shell (Lee, Oct 2026). One "Chats" area with four tabs — Active, Missed,
 * Tickets, Resolution Notes — plus a single "My chats only" toggle that applies
 * to whichever tab you're on. Replaces the old separate sidebar items.
 *
 *  • Active   → /queue (all) or /my-chats (mine). The toggle swaps the route so
 *               each view keeps its own open-chat deep links.
 *  • Missed / Tickets / Resolution Notes → the toggle sets ?mine=1, which the
 *               route element reads and passes to the view as `mineOnly`.
 *
 * The shell is a thin strip; each view renders its own content below it.
 */
type Tab = 'active' | 'missed' | 'tickets' | 'resolutions'

const TABS: Array<{ key: Tab; label: string; path: string }> = [
  { key: 'active', label: 'Active', path: '/queue' },
  { key: 'missed', label: 'Missed', path: '/missed' },
  { key: 'tickets', label: 'Tickets', path: '/tickets' },
  { key: 'resolutions', label: 'Resolution Notes', path: '/resolutions' },
]

function tabOf(pathname: string): Tab {
  if (pathname.startsWith('/missed')) return 'missed'
  if (pathname.startsWith('/tickets')) return 'tickets'
  if (pathname.startsWith('/resolutions')) return 'resolutions'
  return 'active' // /queue, /my-chats
}

export const ChatsShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [sp] = useSearchParams()

  const p = location.pathname
  const tab = tabOf(p)
  const mine = tab === 'active' ? p.startsWith('/my-chats') : sp.get('mine') === '1'

  // Preserve an open chat id when the Active toggle swaps /queue <-> /my-chats.
  const activeMatch = p.match(/^\/(?:queue|my-chats)\/([^/?]+)/)
  const activeChatId = activeMatch ? activeMatch[1] : null

  const goTab = (t: Tab) => {
    if (t === 'active') {
      navigate(mine ? '/my-chats' : '/queue')
    } else {
      navigate(mine ? `${TABS.find((x) => x.key === t)!.path}?mine=1` : TABS.find((x) => x.key === t)!.path)
    }
  }

  const setMine = (on: boolean) => {
    if (tab === 'active') {
      const base = on ? '/my-chats' : '/queue'
      navigate(activeChatId ? `${base}/${activeChatId}` : base)
    } else {
      const next = new URLSearchParams(sp)
      if (on) next.set('mine', '1')
      else next.delete('mine')
      const qs = next.toString()
      navigate(qs ? `${p}?${qs}` : p, { replace: true })
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* One cohesive header: the area title + the "My chats only" toggle on top,
          the tab row beneath — so the hierarchy reads title > tabs > content. */}
      <div className="px-6 pt-4 bg-surface border-b border-border shrink-0">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-txt-primary">Chats</h1>
          <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none" title="Show only chats assigned to me">
            <span className="text-xs font-medium text-txt-secondary">My chats only</span>
            <button
              type="button"
              role="switch"
              aria-checked={mine}
              onClick={() => setMine(!mine)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${mine ? 'bg-brand-600' : 'bg-surface-tertiary dark:bg-white/15'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${mine ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </label>
        </div>
        <div className="flex items-center gap-1 mt-3 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => goTab(t.key)}
              className={`px-3.5 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                tab === t.key
                  ? 'border-brand-600 text-brand-700 dark:text-brand-400 dark:border-brand-400'
                  : 'border-transparent text-txt-secondary hover:text-txt-primary'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  )
}
